import * as Sentry from "@sentry/react";
import type React from "react";
import { ErrorFallback } from "./error-fallback";

// Global type augmentation for PostHog
declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
    };
  }
}

interface AppError extends Error {
  code: string;
  details?: Record<string, unknown>;
  timestamp?: string;
}

interface GlobalErrorBoundaryProps {
  children: React.ReactNode;
}

export function GlobalErrorBoundary({ children }: GlobalErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError, eventId }) => (
        <ErrorFallback error={error} resetError={resetError} eventId={eventId} source="global" />
      )}
      beforeCapture={(scope, error) => {
        scope.setTag("errorBoundary", "GlobalErrorBoundary");
        scope.setLevel("error");
        scope.setContext("errorInfo", {
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          stackTrace: error instanceof Error ? error.stack : undefined,
        });

        // Add additional context for app errors
        if (error instanceof Error && "code" in error) {
          const appError = error as AppError;
          scope.setTag("errorCode", appError.code);
          scope.setContext("appErrorDetails", appError.details || {});
        }
      }}
      onError={(error, errorInfo) => {
        // Enhanced logging in development
        if (import.meta.env.DEV) {
          console.error("🚨 Global Error Boundary:", { error, errorInfo });
        }

        // Track error analytics
        if (typeof window !== "undefined" && window.posthog) {
          window.posthog.capture("error_boundary_triggered", {
            error_message: error instanceof Error ? error.message : String(error),
            error_stack: error instanceof Error ? error.stack : undefined,
            component_stack: typeof errorInfo === "string" ? errorInfo : String(errorInfo),
            url: window.location.href,
          });
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
